// Copyright © 2019-2026 Focal Systems. All rights reserved.

import type { PaginationState, SortingState } from "@tanstack/react-table";
import { Filter, MoreHorizontal, Pencil, Plus, Star, Trash2, X } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type TaskAvailabilityEntry,
  type TaskAvailabilitySortBy,
  type TaskKind,
  type TaskScopeKind,
  useTaskAvailabilityQuery,
} from "@/api/hooks/use-tasks";
import { DataTable, type DataTableColumnDef } from "@/components/data-table";
import { ChainLink } from "@/components/shared/chain-link";
import { CodeHighlight } from "@/components/shared/code-highlight";
import { DateValue } from "@/components/shared/datetime-value";
import { FilterSelect } from "@/components/shared/filter-select";
import { FilterToggle } from "@/components/shared/filter-toggle";
import { ReadOnlySwitch } from "@/components/shared/read-only-switch";
import { RowId } from "@/components/shared/row-id";
import { StatusIndicator } from "@/components/shared/status-indicator";
import { StoreLink } from "@/components/shared/store-link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DeleteTaskAvailabilityDialog } from "@/features/tasks/delete-task-availability-dialog";
import { TaskAvailabilityDialog } from "@/features/tasks/task-availability-dialog";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { getDefaultPageSize } from "@/lib/preferences";

interface TaskAvailabilityTabProps {
  subtype: string;
  /** Which table the rows come from, and so what can be done to them. */
  kind: TaskKind;
  /** The tab only fetches once it is the one on screen. */
  active: boolean;
}

/**
 * Where a task is switched on.
 *
 * For a custom task each `custom_task_availability` row scopes the definition to a chain or a
 * store and carries the rules that gate it there, plus the priority the Action Tool sorts the task
 * list by. A built-in type has no such table: the same three decisions live on its
 * `action_task_settings` row, one per store, so those are listed and edited here instead — but a
 * row cannot be added or removed, since it is provisioned along with the store, and it carries no
 * rules and no scope wider than that one store.
 */
export function TaskAvailabilityTab({ subtype, kind, active }: TaskAvailabilityTabProps) {
  const { t } = useTranslation("back-office");
  const { hasPermission } = useAuth();
  const { data: context, isLoading: contextLoading } = useBackofficeContext();
  const isBuiltIn = kind === "builtIn";
  const canEdit = hasPermission(PAGE_PERMISSIONS.tasksAvailabilityEdit);
  const [editing, setEditing] = useState<TaskAvailabilityEntry | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState<TaskAvailabilityEntry | null>(null);
  const [sorting, setSorting] = useState<SortingState>([{ id: "priority", desc: true }]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: getDefaultPageSize(),
  });
  const [search, setSearch] = useState("");
  const [scopeFilter, setScopeFilter] = useState("");
  const [chainFilter, setChainFilter] = useState("");
  const [storeFilter, setStoreFilter] = useState("");
  const [onlyActive, setOnlyActive] = useState(false);
  const [onlyDefault, setOnlyDefault] = useState(false);

  const isFiltered = !!search || !!scopeFilter || !!chainFilter || !!storeFilter || onlyActive || onlyDefault;
  const resetPage = useCallback(() => setPagination((prev) => ({ ...prev, pageIndex: 0 })), []);
  const clearFilters = useCallback(() => {
    setSearch("");
    setScopeFilter("");
    setChainFilter("");
    setStoreFilter("");
    setOnlyActive(false);
    setOnlyDefault(false);
    resetPage();
  }, [resetPage]);

  const { data, isLoading, refetch, isFetching } = useTaskAvailabilityQuery(
    subtype,
    {
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      search: search || undefined,
      sortBy: (sorting[0]?.id ?? "priority") as TaskAvailabilitySortBy,
      orderBy: sorting[0]?.desc ? "desc" : "asc",
      scope: (scopeFilter || undefined) as TaskScopeKind | undefined,
      chainName: chainFilter || undefined,
      storeName: storeFilter || undefined,
      isActive: onlyActive || undefined,
      isDefault: onlyDefault || undefined,
    },
    active,
  );

  const rows = useMemo(() => (data?.availability ?? []) as TaskAvailabilityEntry[], [data?.availability]);

  const columns = useMemo<DataTableColumnDef<TaskAvailabilityEntry>[]>(
    () => [
      {
        accessorKey: "availabilityId",
        header: t("tasks.availability.columns.id"),
        size: 80,
        enableHiding: false,
        // The two tables key their rows differently, so there is no shared column to sort on
        enableSorting: false,
        meta: {
          columnLabel: t("tasks.availability.columns.id"),
          className: "pl-4",
          forceWidth: true,
          sticky: "left",
        },
        cell: ({ row }) => <RowId id={row.original.availabilityId} />,
      },
      {
        id: "scope",
        header: t("tasks.availability.columns.scope"),
        enableSorting: false,
        meta: { columnLabel: t("tasks.availability.columns.scope") },
        cell: ({ row }) => {
          if (row.original.storeName) {
            return (
              <StoreLink
                storeName={row.original.storeName}
                legibleName={row.original.storeLegibleName ?? row.original.storeName}
              />
            );
          }
          if (row.original.chainName) {
            return (
              <ChainLink
                chainName={row.original.chainName}
                legibleName={row.original.chainLegibleName ?? row.original.chainName}
              />
            );
          }
          return <span className="text-muted-foreground">{t("tasks.scope.global")}</span>;
        },
      },
      ...(isBuiltIn
        ? []
        : [
            {
              accessorKey: "scope",
              header: t("tasks.availability.columns.level"),
              size: 100,
              // Scope is derived from the chain/store columns, not a column the API can sort on
              enableSorting: false,
              meta: { columnLabel: t("tasks.availability.columns.level") },
              cell: ({ row }) => (
                <Badge variant="outline" className="font-normal lowercase">
                  {t(`tasks.scope.${row.original.scope}`)}
                </Badge>
              ),
            } satisfies DataTableColumnDef<TaskAvailabilityEntry>,
          ]),
      {
        accessorKey: "priority",
        header: t("tasks.availability.columns.priority"),
        size: 90,
        meta: { columnLabel: t("tasks.availability.columns.priority") },
        cell: ({ row }) => <span className="font-mono tabular-nums">{row.original.priority}</span>,
      },
      {
        id: "isDefault",
        header: t("tasks.availability.columns.default"),
        size: 90,
        enableSorting: false,
        meta: { columnLabel: t("tasks.availability.columns.default") },
        // Reflects the stored flag; the row's Edit action is where it changes
        cell: ({ row }) => <ReadOnlySwitch checked={row.original.isDefault} />,
      },
      ...(isBuiltIn
        ? []
        : [
            {
              id: "rules",
              header: t("tasks.availability.columns.rules"),
              enableSorting: false,
              meta: { columnLabel: t("tasks.availability.columns.rules") },
              cell: ({ row }) =>
                row.original.rules?.length ? (
                  <CodeHighlight
                    value={JSON.stringify(row.original.rules)}
                    lang="json"
                    className="[&_pre]:min-w-0 [&_pre]:whitespace-pre-wrap [&_pre]:break-words [&_pre]:text-xs"
                  />
                ) : (
                  <span className="text-muted-foreground">{t("tasks.availability.noRules")}</span>
                ),
            } satisfies DataTableColumnDef<TaskAvailabilityEntry>,
          ]),
      {
        accessorKey: "updatedAt",
        header: t("tasks.availability.columns.updatedAt"),
        meta: { columnLabel: t("tasks.availability.columns.updatedAt") },
        cell: ({ row }) => <DateValue value={row.original.updatedAt} />,
      },
      {
        id: "isActive",
        header: t("tasks.availability.columns.status"),
        size: 80,
        enableSorting: false,
        meta: { columnLabel: t("tasks.availability.columns.status") },
        cell: ({ row }) => <StatusIndicator active={row.original.isActive} />,
      },
      ...(canEdit
        ? [
            {
              // `actions` is the id DataTable looks for to host the column-visibility switcher.
              id: "actions",
              size: 50,
              meta: { forceWidth: true, sticky: "right" },
              enableSorting: false,
              enableHiding: false,
              cell: ({ row }) => (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" sideOffset={-32} className="dropdown-overlap">
                    <DropdownMenuItem
                      onSelect={() => {
                        setEditing(row.original);
                        setDialogOpen(true);
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                      {t("buttons.edit")}
                    </DropdownMenuItem>
                    {!isBuiltIn && (
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onSelect={() => setDeleting(row.original)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                        {t("buttons.remove")}
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              ),
            } satisfies DataTableColumnDef<TaskAvailabilityEntry>,
          ]
        : []),
    ],
    [t, canEdit, isBuiltIn],
  );

  return (
    <div className="p-6">
      <DataTable
        storageKey="task-availability"
        columns={columns}
        data={rows}
        totalCount={data?.count ?? 0}
        isLoading={isLoading}
        pagination={pagination}
        onPaginationChange={setPagination}
        sorting={sorting}
        onSortingChange={setSorting}
        onRefresh={refetch}
        isRefreshing={isFetching}
        search={search}
        onSearchChange={setSearch}
        searchDebounceMs={1000}
        searchPlaceholder={t("tasks.availability.searchPlaceholder")}
        filters={
          <>
            {!isBuiltIn && (
              <FilterSelect
                label={t("tasks.availability.columns.level")}
                value={scopeFilter}
                onChange={(value) => {
                  setScopeFilter(value);
                  resetPage();
                }}
                options={[
                  { label: t("tasks.scope.global"), value: "global" },
                  { label: t("tasks.scope.chain"), value: "chain" },
                  { label: t("tasks.scope.store"), value: "store" },
                ]}
                searchPlaceholder={t("tasks.availability.filter.searchLevels")}
                emptyText={t("tasks.availability.filter.noLevels")}
              />
            )}
            <FilterSelect
              label={t("tasks.availability.filter.chain")}
              value={chainFilter}
              onChange={(value) => {
                setChainFilter(value);
                resetPage();
              }}
              options={(context?.chains ?? []).map((chain) => ({ label: chain.display, value: chain.value }))}
              searchPlaceholder={t("tasks.availability.filter.searchChains")}
              emptyText={t("tasks.availability.filter.noChains")}
              loading={contextLoading}
            />
            <FilterSelect
              label={t("tasks.availability.filter.store")}
              value={storeFilter}
              onChange={(value) => {
                setStoreFilter(value);
                resetPage();
              }}
              options={(context?.stores ?? []).map((store) => ({ label: store.display, value: store.value }))}
              searchPlaceholder={t("tasks.availability.filter.searchStores")}
              emptyText={t("tasks.availability.filter.noStores")}
              loading={contextLoading}
            />
            <FilterToggle
              active={onlyDefault}
              onChange={() => {
                setOnlyDefault((prev) => !prev);
                resetPage();
              }}
              icon={Star}
              label={t("tasks.availability.columns.default")}
            />
            <FilterToggle
              active={onlyActive}
              onChange={() => {
                setOnlyActive((prev) => !prev);
                resetPage();
              }}
              icon={Filter}
              label={t("common.active")}
            />
            {isFiltered && (
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 text-muted-foreground hover:text-foreground"
                onClick={clearFilters}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </>
        }
        toolbar={
          canEdit && !isBuiltIn ? (
            <Button
              onClick={() => {
                setEditing(null);
                setDialogOpen(true);
              }}
            >
              <Plus className="h-4 w-4" />
              {t("tasks.availability.addButton")}
            </Button>
          ) : undefined
        }
      />

      <TaskAvailabilityDialog
        subtype={subtype}
        entry={editing}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
      <DeleteTaskAvailabilityDialog subtype={subtype} entry={deleting} onClose={() => setDeleting(null)} />
    </div>
  );
}
